import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";

export default function PasswordRecovery() {
  const { pathname } = useLocation();
  const resetting = pathname === "/reset-password";
  // Keep the token in memory and remove it from the address bar. The email
  // uses a fragment so the token is never sent to the hosting server in a URL.
  const [token] = useState(() => {
    const value = new URLSearchParams(window.location.hash.slice(1)).get("token") || "";
    return value;
  });
  useEffect(() => {
    if (token) window.history.replaceState(window.history.state, "", pathname);
  }, [token, pathname]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (resetting && password !== confirmation) {
      setError("The passwords do not match."); return;
    }
    if (resetting && new TextEncoder().encode(password).length > 72) {
      setError("Use a password of at most 72 UTF-8 bytes."); return;
    }
    setBusy(true);
    try {
      await api.post(resetting ? "/auth/reset-password" : "/auth/forgot-password",
        resetting ? { token, password } : { email });
      setPassword(""); setConfirmation(""); setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We couldn't complete that request. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#FFFDF9] px-4 py-12 text-[#2C2623]">
      <main className="max-w-md mx-auto">
        <Link to="/" className="font-serif text-2xl font-bold">HerpMates</Link>
        <Card className="mt-8 rounded-3xl border-[#E8DEC8] bg-white p-8">
          <h1 className="font-serif text-3xl font-bold mb-4">{resetting ? "Reset your password" : "Forgot your password?"}</h1>
          {done ? (
            <div role="status" className="space-y-4">
              <p>{resetting ? "Password updated. Sign in with your new password." : "If an account exists for that email, a reset link will arrive shortly. Check your inbox and spam folder. The link expires in 30 minutes."}</p>
              <Link className="text-[#D95D39] underline" to="/">Back to sign in</Link>
            </div>
          ) : resetting && !token ? (
            <div className="space-y-4"><p>This reset link is missing or has expired.</p><Link className="text-[#D95D39] underline" to="/forgot-password">Request a new link</Link></div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {!resetting ? <><p>Enter the email you used for HerpMates. We'll send a one-use reset link if it has a password account.</p><label className="block" htmlFor="recovery-email">Email</label><Input id="recovery-email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} /></> : <>
                <label className="block" htmlFor="new-password">New password</label><Input id="new-password" type="password" autoComplete="new-password" required minLength={8} maxLength={72} value={password} onChange={e => setPassword(e.target.value)} />
                <p className="text-sm">Use at least 8 characters.</p>
                <label className="block" htmlFor="confirm-password">Confirm new password</label><Input id="confirm-password" type="password" autoComplete="new-password" required minLength={8} maxLength={72} value={confirmation} onChange={e => setConfirmation(e.target.value)} />
              </>}
              {error && <div role="alert" className="text-red-700"><p>{error}</p>{resetting && <Link to="/forgot-password" className="underline">Request a new reset link</Link>}</div>}
              <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#D95D39] hover:bg-[#C24C29]">{busy ? "Please wait…" : resetting ? "Update password" : "Send reset link"}</Button>
              <Link className="block text-center text-sm underline" to="/">Back to sign in</Link>
            </form>
          )}
        </Card>
      </main>
    </div>
  );
}
